import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type TooltipProps = {
    label: string;
    children: React.ReactNode;
    // wrapper class; the default styles hoverable text (dotted underline)
    className?: string;
};

// Instant tooltip shown on hover, keyboard focus and tap (touch devices),
// unlike the native title attribute which needs a mouse and a hover delay.
// The bubble is rendered into document.body via a portal: ancestors with
// transform/backdrop-filter (e.g. the modal) would otherwise re-anchor
// position: fixed and shift the bubble away from the text.
export function Tooltip({ label, children, className }: TooltipProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const bubbleRef = useRef<HTMLSpanElement>(null);
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

    // keep the bubble inside the viewport when the anchor sits near an edge
    useLayoutEffect(() => {
        const rect = bubbleRef.current?.getBoundingClientRect();
        if (!pos || !rect) return;

        const margin = 8;
        let shift = 0;
        if (rect.left < margin) {
            shift = margin - rect.left;
        } else if (rect.right > window.innerWidth - margin) {
            shift = window.innerWidth - margin - rect.right;
        }
        if (shift) setPos({ x: pos.x + shift, y: pos.y });
    }, [pos]);

    const show = () => {
        const rect = ref.current?.getBoundingClientRect();
        if (rect) setPos({ x: rect.left + rect.width / 2, y: rect.bottom });
    };
    const hide = () => setPos(null);

    return (
        <span
            ref={ref}
            className={className ?? "name-tooltip"}
            tabIndex={0}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            // click only shows — a tap fires mouseenter+click together, so a
            // toggle would hide the bubble right away; tapping away blurs it
            onClick={show}
        >
            {children}
            {pos &&
                createPortal(
                    <span
                        ref={bubbleRef}
                        className="tooltip-bubble"
                        role="tooltip"
                        style={{ left: pos.x, top: pos.y }}
                    >
                        {label}
                    </span>,
                    document.body
                )}
        </span>
    );
}